import FloatingTooltip from "@components/common/FloatingTooltip";
import { useTranslation } from "react-i18next";

import { differenceInCalendarDays, format } from "date-fns";
import React from "react";
import { Route, Schedule } from "@gen/travel/v1/common_pb";
import { Place } from "@gen/place/v1/place_pb";
import { localDateTimeToDate } from "@utils/date";

type TravelTimelineProps = {
  className?: string;
  journey: { schedule?: Schedule; route?: Route };
  showDurationInDays?: boolean;
  showDuration?: boolean;
  children?: React.ReactNode;
  disabled?: boolean;
};

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export default function TravelTimeline({
  className,
  journey,
  showDurationInDays,
  showDuration = true,
  children,
  disabled,
}: TravelTimelineProps) {
  const { t } = useTranslation();

  if (
    !journey.schedule?.departure ||
    !journey.schedule?.arrival ||
    !journey.route?.origin ||
    !journey.route?.destination
  ) {
    return;
  }
  const departure = localDateTimeToDate(journey.schedule?.departure);
  const arrival = localDateTimeToDate(journey.schedule?.arrival);
  const daysDifference = differenceInCalendarDays(arrival, departure);
  const duration = journey.schedule?.duration;
  const hours = Math.floor(duration / 3600) || 0;
  const minutes = Math.floor((duration % 3600) / 60) || 0;

  return (
    <div
      className={`flex min-w-0 text-gray-500 max-h-full ${
        disabled ? "opacity-50" : ""
      } ${className}`}
    >
      {departure && arrival && (
        <div className="flex flex-col shrink-0 justify-between items-end max-w-[60px] text-lg">
          {departure && (
            <div className="text-sm">{format(departure, "HH:mm")}</div>
          )}
          {showDuration && (
            <div className="py-1 text-xs text-right">
              {showDurationInDays
                ? `${daysDifference}${t("results.dayAbbreviation")}`
                : `${hours}${t("results.hourAbbreviation")}${
                    minutes < 10 ? "0" : ""
                  }${minutes}`}
            </div>
          )}
          {arrival && (
            <div className="text-sm flex items-center">
              {format(arrival, "HH:mm")}
              {daysDifference > 0 && (
                <span className="text-xs ml-1">+{daysDifference}</span>
              )}
            </div>
          )}
        </div>
      )}
      <div className="flex flex-col shrink-0 items-center justify-center px-2 py-1">
        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
        <div className="flex-1 border-l-2 border-blue-500"></div>
        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
      </div>
      <div className="flex flex-col justify-between min-w-0 flex-1 relative max-h-full gap-4">
        <PlaceItem place={journey.route?.origin} />
        {children}
        <PlaceItem place={journey.route?.destination} />
      </div>
    </div>
  );
}

interface PlaceItemProps {
  place: Place;
}

function PlaceItem({ place }: PlaceItemProps) {
  const { t } = useTranslation();

  return (
    <FloatingTooltip content={place.name}>
      <div className="whitespace-nowrap overflow-hidden text-ellipsis h-[22px] leading-[22px] hover:tooltip">
        <span className="text-base">
          {place.name ?? capitalize(t("UNKNOWN_PLACE"))}
        </span>
      </div>
    </FloatingTooltip>
  );
}
